import { z } from 'zod';
import { nutritionPerSchema } from '../units.js';
/**
 * Restaurant menus read by the admin tool and approved before they land
 * (ADR-029). Nothing here reaches `food` until an admin approves a row.
 */
export const restaurantImportStatusSchema = z.enum(['queued', 'reading', 'ready', 'failed', 'closed']);
export const restaurantItemStateSchema = z.enum(['proposed', 'approved', 'rejected']);
export const restaurantItemDiffSchema = z.enum(['new', 'changed', 'unchanged', 'gone']);
export const startRestaurantImportSchema = z.object({
    brand: z.string().trim().min(1).max(120),
    url: z.string().url().max(2000),
});
/**
 * Pages pasted in by an admin, a piece at a time (ADR-031). Each piece is
 * what one paste put on the clipboard: its plain text, and its HTML when
 * that holds a table, which keeps the columns.
 */
export const pastedPieceSchema = z.object({
    text: z.string().max(300_000),
    html: z.string().max(2_000_000).nullable(),
});
export const pasteRestaurantImportSchema = z.object({
    brand: z.string().trim().min(1).max(120),
    /** Where the pages came from, for attribution. */
    sourceUrl: z.string().url().max(2000).nullable().optional(),
    pieces: z
        .array(pastedPieceSchema)
        .min(1)
        .max(60)
        .refine((pieces) => pieces.some((p) => p.text.trim() || p.html?.trim()), 'Paste something to read first.'),
});
const figure = z.number().nullable();
/** The figures a row carries: per 100 g, or per one item (ADR-028). */
export const restaurantFiguresSchema = z.object({
    energyKcal: figure,
    energyKj: figure,
    proteinG: figure,
    fatG: figure,
    satFatG: figure,
    carbG: figure,
    sugarG: figure,
    fibreG: figure,
    saltG: figure,
});
export const restaurantImportItemSchema = restaurantFiguresSchema.extend({
    id: z.string().uuid(),
    position: z.number().int(),
    name: z.string(),
    section: z.string().nullable(),
    sourceUrl: z.string(),
    sourceRef: z.string().nullable(),
    /**
     * Read by code, from a layout the model described or numbers it pointed at
     * (ADR-031), or transcribed from an image.
     */
    readBy: z.enum(['layout', 'transcription', 'library']),
    nutritionPer: nutritionPerSchema,
    portionG: z.number().nullable(),
    checks: z.array(z.object({ code: z.string(), message: z.string() })),
    state: restaurantItemStateSchema,
    diff: restaurantItemDiffSchema,
    existingFoodId: z.string().uuid().nullable(),
    /** The library figures this row would replace, for a changed dish. */
    existing: restaurantFiguresSchema.extend({ nutritionPer: nutritionPerSchema, portionG: z.number().nullable() }).nullable(),
    foodId: z.string().uuid().nullable(),
    edited: z.boolean(),
});
export const restaurantImportSchema = z.object({
    id: z.string().uuid(),
    brand: z.string(),
    startUrl: z.string(),
    status: restaurantImportStatusSchema,
    stage: z.string().nullable(),
    error: z.string().nullable(),
    jobId: z.string().uuid().nullable(),
    documents: z.array(z.object({ url: z.string(), contentType: z.string(), readAs: z.string() })),
    notes: z.array(z.string()),
    counts: z.object({
        proposed: z.number().int(),
        approved: z.number().int(),
        rejected: z.number().int(),
        /** Proposed rows with a failed check, approved only one at a time. */
        flagged: z.number().int(),
    }),
    createdAt: z.string(),
    closedAt: z.string().nullable(),
});
export const restaurantImportDetailSchema = restaurantImportSchema.extend({
    items: z.array(restaurantImportItemSchema),
});
const editedFigure = z.number().min(0).max(100000).nullable().optional();
/** A reviewer's correction to one row, or a decision on it. */
export const restaurantImportItemPatchSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    section: z.string().trim().max(120).nullable().optional(),
    nutritionPer: nutritionPerSchema.optional(),
    portionG: z.number().positive().max(10000).nullable().optional(),
    energyKcal: editedFigure,
    energyKj: editedFigure,
    proteinG: editedFigure,
    fatG: editedFigure,
    satFatG: editedFigure,
    carbG: editedFigure,
    sugarG: editedFigure,
    fibreG: editedFigure,
    saltG: editedFigure,
    state: z.enum(['proposed', 'rejected']).optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
/**
 * Approve rows into the shared library. More than one row at a time may not
 * include a row with a failed check (ADR-029): those are approved one by one.
 */
export const approveRestaurantItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
});
export const approveRestaurantItemsResultSchema = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    withdrawn: z.number().int(),
});
//# sourceMappingURL=restaurant.js.map